import './Tooltip.css'
import { useState, useEffect, useRef, useCallback } from 'react'
import { Transition, TransitionChild } from '@headlessui/react'
import { createPortal } from 'react-dom'
import cx from 'classnames'

type Variant = 'default' | 'info' | 'danger' | 'warning'

const variantClasses: Record<Variant, string> = {
  default: 'tooltip-default',
  info: 'tooltip-info',
  danger: 'tooltip-danger',
  warning: 'tooltip-warning'
}

export function Tooltip({
  position = 'top',
  children,
  content,
  variant = 'default'
}: {
  position?: 'top' | 'bottom'
  children: React.ReactNode
  content: React.ReactNode
  variant?: Variant
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 })
  const triggerRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  const updatePosition = useCallback(() => {
    if (!isOpen || !triggerRef.current || !tooltipRef.current) {return}

    const triggerRect = triggerRef.current.getBoundingClientRect()
    const tooltipRect = tooltipRef.current.getBoundingClientRect()
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft

    // Center the tooltip horizontally relative to the trigger
    const left = triggerRect.left + scrollLeft + (triggerRect.width - tooltipRect.width) / 2

    // Calculate available space above and below
    const spaceAbove = triggerRect.top + scrollTop
    const spaceBelow = window.innerHeight + scrollTop - (triggerRect.bottom + scrollTop)
    const tooltipHeight = tooltipRect.height + 8 // Add padding

    // Determine best position based on available space
    const hasEnoughSpaceAbove = spaceAbove >= tooltipHeight
    const hasEnoughSpaceBelow = spaceBelow >= tooltipHeight

    // If specified position doesn't have enough space, try the other position
    const bestPosition =
      position === 'bottom'
        ? hasEnoughSpaceBelow
          ? 'bottom'
          : 'top'
        : hasEnoughSpaceAbove
          ? 'top'
          : 'bottom'

    // Calculate vertical position based on best position
    const top =
      bestPosition === 'bottom'
        ? triggerRect.top + scrollTop + triggerRect.height + 8
        : triggerRect.top + scrollTop - tooltipRect.height - 8

    // Ensure tooltip stays within viewport
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    // Adjust horizontal position if tooltip would overflow
    let adjustedLeft = left
    if (adjustedLeft < 8) {
      adjustedLeft = 8 // 8px from left edge
    } else if (adjustedLeft + tooltipRect.width > viewportWidth) {
      adjustedLeft = viewportWidth - tooltipRect.width - 8 // 8px from right edge
    }

    // Adjust vertical position if tooltip would overflow
    let adjustedTop = top
    if (adjustedTop < 8) {
      adjustedTop = 8 // 8px from top edge
    } else if (adjustedTop + tooltipRect.height > viewportHeight + scrollTop) {
      adjustedTop = viewportHeight + scrollTop - tooltipRect.height - 8 // 8px from bottom edge
    }

    setTooltipPosition({ top: adjustedTop, left: adjustedLeft })
  }, [isOpen, position])

  useEffect(() => {
    if (isOpen) {
      updatePosition()
      window.addEventListener('resize', updatePosition)
      return () => {
        window.removeEventListener('resize', updatePosition)
      }
    }
  }, [isOpen, position, updatePosition])

  return (
    <div className="tooltip-trigger" ref={triggerRef}>
      <div onMouseEnter={() => setIsOpen(true)} onMouseLeave={() => setIsOpen(false)}>
        {children}
      </div>
      {isOpen &&
        createPortal(
          <Transition show={isOpen} appear>
            <TransitionChild
              enter="transition duration-300 ease-out"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition duration-300 ease-out"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div
                ref={tooltipRef}
                className={cx('tooltip-content', variantClasses[variant])}
                style={{
                  top: `${tooltipPosition.top}px`,
                  left: `${tooltipPosition.left}px`
                }}
              >
                {content}
              </div>
            </TransitionChild>
          </Transition>,
          document.body
        )}
    </div>
  )
}
